export const PROFILE_FIELD_TYPES = {
  SINGLE_SELECT: 'single_select',
  TEXT_INPUT: 'text_input',
  MULTI_SELECT: 'multi_select',
  DATE: 'date',
};

export const PROFILE_FIELD_TYPE_OPTIONS = [
  { value: PROFILE_FIELD_TYPES.SINGLE_SELECT, label: 'Single select' },
  { value: PROFILE_FIELD_TYPES.TEXT_INPUT, label: 'Text input' },
  { value: PROFILE_FIELD_TYPES.MULTI_SELECT, label: 'Multi-select' },
  { value: PROFILE_FIELD_TYPES.DATE, label: 'Date' },
];

/**
 * Matches server-side legacy behavior: null/single_select + options → dropdown;
 * no options → free text (pre–field_type rows).
 */
export function getEffectiveProfileFieldType(field) {
  const stored = field?.field_type;
  const hasOptions = (field?.options?.length ?? 0) > 0;
  const types = Object.values(PROFILE_FIELD_TYPES);

  if (stored && types.includes(stored)) {
    if (stored === PROFILE_FIELD_TYPES.SINGLE_SELECT && !hasOptions) {
      return PROFILE_FIELD_TYPES.TEXT_INPUT;
    }
    if (
      (stored === PROFILE_FIELD_TYPES.TEXT_INPUT ||
        stored === PROFILE_FIELD_TYPES.DATE) &&
      hasOptions
    ) {
      return PROFILE_FIELD_TYPES.SINGLE_SELECT;
    }
    return stored;
  }

  if (hasOptions) {
    return PROFILE_FIELD_TYPES.SINGLE_SELECT;
  }
  return PROFILE_FIELD_TYPES.TEXT_INPUT;
}

/** Grid width for profile field forms — multi-select needs more horizontal space */
export function getProfileFieldGridSize(field) {
  const type = getEffectiveProfileFieldType(field);
  if (type === PROFILE_FIELD_TYPES.MULTI_SELECT) {
    return { xs: 12, sm: 12, md: 12 };
  }
  return { xs: 12, sm: 6, md: 4 };
}

export function isValidCalendarDate(isoDate) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
    return false;
  }
  const [y, m, d] = isoDate.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return (
    date.getUTCFullYear() === y &&
    date.getUTCMonth() === m - 1 &&
    date.getUTCDate() === d
  );
}

export function parseNorthAmericanDateToIso(input) {
  if (!input || typeof input !== 'string') {
    return null;
  }
  const trimmed = input.trim();
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
  if (!match) {
    return null;
  }
  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }
  const iso = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  return isValidCalendarDate(iso) ? iso : null;
}

export function formatIsoDateToNorthAmerican(isoDate) {
  if (!isoDate || typeof isoDate !== 'string') {
    return '';
  }
  const datePart = isoDate.split('T')[0];
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart);
  if (!match) {
    return isoDate;
  }
  return `${Number(match[2])}/${Number(match[3])}/${match[1]}`;
}

export function parseMultiSelectStoredValue(value) {
  if (Array.isArray(value)) {
    return value;
  }
  if (!value || typeof value !== 'string') {
    return [];
  }
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [value];
  } catch {
    return value ? [value] : [];
  }
}

/** Unique option rows by display name (avoids duplicates after field merge). */
export function dedupeProfileFieldOptions(options) {
  const seen = new Set();
  return (options || []).filter((opt) => {
    const name = String(opt?.option_name ?? opt?.name ?? '').trim();
    if (!name || seen.has(name)) {
      return false;
    }
    seen.add(name);
    return true;
  });
}

/** Current option labels for single/multi select fields */
export function getProfileFieldOptionNames(field) {
  return dedupeProfileFieldOptions(field?.options).map(
    (option) => option.option_name ?? option.name,
  );
}

/** Drop stored values that are not in the field's current option list */
export function sanitizeSingleSelectValue(field, value) {
  const optionNames = getProfileFieldOptionNames(field);
  if (!optionNames.length) {
    return value === null || value === undefined ? '' : String(value).trim();
  }
  const str = value === null || value === undefined ? '' : String(value).trim();
  return optionNames.includes(str) ? str : '';
}

export function sanitizeMultiSelectValue(field, value) {
  const parsed = parseMultiSelectStoredValue(value);
  const optionNames = getProfileFieldOptionNames(field);
  if (!optionNames.length) {
    return parsed;
  }
  return parsed.filter((name) => optionNames.includes(name));
}

export function isProfileFieldValueEmpty(field, value) {
  const type = getEffectiveProfileFieldType(field);
  if (type === PROFILE_FIELD_TYPES.MULTI_SELECT) {
    return sanitizeMultiSelectValue(field, value).length === 0;
  }
  if (type === PROFILE_FIELD_TYPES.SINGLE_SELECT) {
    return !sanitizeSingleSelectValue(field, value);
  }
  return value === null || value === undefined || String(value).trim() === '';
}

function isProfileFieldRequired(field) {
  return field?.is_checked === 1 || field?.is_checked === true;
}

function profileFieldDisplayName(field) {
  return field?.profile_field_name || 'This field';
}

function trimProfileFieldInput(value) {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value).trim();
}

const INVALID_DATE_MSG = 'Enter a valid date in MM/DD/YYYY format';

function validateMultiSelectClient(field, value, isRequired, name) {
  const selected = sanitizeMultiSelectValue(field, value);
  if (isRequired && selected.length === 0) {
    return `${name} is required`;
  }
  return '';
}

function validateSingleSelectClient(field, value, isRequired, name) {
  const effective = sanitizeSingleSelectValue(field, value);
  if (isRequired && !effective) {
    return `${name} is required`;
  }
  return '';
}

function validateDateClient(_field, value, isRequired, name) {
  const str = trimProfileFieldInput(value);
  if (!str) {
    return isRequired ? `${name} is required` : '';
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    const iso = str.split('T')[0];
    return isValidCalendarDate(iso) ? '' : INVALID_DATE_MSG;
  }
  return parseNorthAmericanDateToIso(str) ? '' : INVALID_DATE_MSG;
}

function validateScalarClient(field, value, isRequired, name) {
  if (isRequired && isProfileFieldValueEmpty(field, value)) {
    return `${name} is required`;
  }
  return '';
}

const CLIENT_FIELD_VALIDATORS = {
  [PROFILE_FIELD_TYPES.MULTI_SELECT]: validateMultiSelectClient,
  [PROFILE_FIELD_TYPES.DATE]: validateDateClient,
  [PROFILE_FIELD_TYPES.SINGLE_SELECT]: validateSingleSelectClient,
};

export function validateProfileFieldValueClient(field, value) {
  const type = getEffectiveProfileFieldType(field);
  const isRequired = isProfileFieldRequired(field);
  const name = profileFieldDisplayName(field);
  const validate = CLIENT_FIELD_VALIDATORS[type];
  return validate
    ? validate(field, value, isRequired, name)
    : validateScalarClient(field, value, isRequired, name);
}

/** Normalize form value before POST to API */
export function serializeProfileFieldValueForApi(field, value) {
  const type = getEffectiveProfileFieldType(field);
  if (type === PROFILE_FIELD_TYPES.MULTI_SELECT) {
    const selected = sanitizeMultiSelectValue(field, value);
    return JSON.stringify(selected);
  }
  if (type === PROFILE_FIELD_TYPES.SINGLE_SELECT) {
    return sanitizeSingleSelectValue(field, value);
  }
  if (type === PROFILE_FIELD_TYPES.DATE) {
    const str = value === null || value === undefined ? '' : String(value).trim();
    if (!str) {
      return '';
    }
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
      return str.split('T')[0];
    }
    return parseNorthAmericanDateToIso(str) || str;
  }
  return value === null || value === undefined ? '' : String(value);
}

/** Map stored API value to form state */
export function deserializeProfileFieldValueForForm(field, storedValue) {
  const type = getEffectiveProfileFieldType(field);
  if (type === PROFILE_FIELD_TYPES.MULTI_SELECT) {
    return sanitizeMultiSelectValue(field, storedValue);
  }
  if (type === PROFILE_FIELD_TYPES.SINGLE_SELECT) {
    return sanitizeSingleSelectValue(field, storedValue);
  }
  if (type === PROFILE_FIELD_TYPES.DATE && storedValue) {
    return formatIsoDateToNorthAmerican(String(storedValue));
  }
  return storedValue ?? '';
}
